/**
 * Shared API Client with automatic token refresh
 *
 * All services should use this shared client to ensure consistent
 * authentication handling and automatic session extension.
 */

import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';

import { API_BASE_URL } from '../config';
import i18n from '../i18n';
import { useToastStore } from '../stores/toastStore';

// Token refresh threshold: refresh if less than 5 minutes remaining
const TOKEN_REFRESH_THRESHOLD_MS = 5 * 60 * 1000; // 5 minutes

// Background poll: re-check token TTL every 5 min so we refresh proactively
// even when the user is idle and no API request triggers the interceptor.
// Access token TTL is 30 min (auth.service.ts ACCESS_TOKEN_TTL), so 5-min
// poll lands within the refresh window every cycle.
const BACKGROUND_REFRESH_POLL_MS = 5 * 60 * 1000;

const IS_DEV = process.env.NODE_ENV === 'development';

// Flag to prevent multiple simultaneous refresh attempts
let isRefreshing = false;
let refreshPromise: Promise<string | null> | null = null;
let backgroundRefreshTimer: ReturnType<typeof setInterval> | null = null;

/**
 * Parse JWT token to extract payload (without verification)
 * Returns null if token is invalid
 */
export const parseJwtPayload = (token: string): { exp?: number; iat?: number; sub?: string } | null => {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;

    const payload = parts[1];
    // Handle base64url encoding
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch {
    return null;
  }
};

/**
 * Check if token is about to expire (within threshold)
 */
export const isTokenExpiringSoon = (token: string): boolean => {
  const payload = parseJwtPayload(token);
  if (!payload?.exp) return true; // If can't parse, assume expiring

  const expiryTime = payload.exp * 1000; // Convert to milliseconds
  const now = Date.now();
  const timeRemaining = expiryTime - now;

  return timeRemaining < TOKEN_REFRESH_THRESHOLD_MS;
};

/**
 * Get remaining time until token expires (in seconds)
 */
export const getTokenRemainingTime = (token: string): number => {
  const payload = parseJwtPayload(token);
  if (!payload?.exp) return 0;

  const expiryTime = payload.exp * 1000;
  const now = Date.now();
  return Math.max(0, Math.floor((expiryTime - now) / 1000));
};

/**
 * Proactively refresh token if it's about to expire
 * Returns new access token or null if refresh fails
 */
export const proactiveRefresh = async (): Promise<string | null> => {
  const refreshToken = localStorage.getItem('refreshToken');
  if (!refreshToken) return null;

  // If already refreshing, wait for that to complete
  if (isRefreshing && refreshPromise) {
    return refreshPromise;
  }

  isRefreshing = true;
  refreshPromise = (async () => {
    // One log per actual refresh attempt — not per concurrent request that
    // observed the expiring token. Dedup is enforced by `isRefreshing` above.
    if (IS_DEV) {
      const currentToken = localStorage.getItem('accessToken');
      const remaining = currentToken ? getTokenRemainingTime(currentToken) : 0;
      console.log(`⏰ Refreshing token (${remaining}s remaining)…`);
    }

    try {
      const response = await axios.post(`${API_BASE_URL}/api/v1/auth/refresh`, {
        refreshToken,
      });

      const { accessToken, refreshToken: newRefreshToken } = response.data.data.tokens;
      localStorage.setItem('accessToken', accessToken);
      localStorage.setItem('refreshToken', newRefreshToken);

      if (IS_DEV) {
        const remainingTime = getTokenRemainingTime(accessToken);
        console.log(`🔄 Token refreshed. New token valid for ${Math.floor(remainingTime / 60)} minutes`);
      }
      return accessToken;
    } catch (error) {
      if (IS_DEV) console.warn('Token refresh failed:', error);
      return null;
    } finally {
      isRefreshing = false;
      refreshPromise = null;
    }
  })();

  return refreshPromise;
};

/**
 * Start the background refresh loop. Idempotent — calling twice is a no-op.
 * Should be invoked once after a successful login (or app boot with stored
 * tokens) and stopped on logout.
 */
export const startBackgroundRefresh = (): void => {
  if (backgroundRefreshTimer) return;
  backgroundRefreshTimer = setInterval(() => {
    const token = localStorage.getItem('accessToken');
    if (!token) {
      stopBackgroundRefresh();
      return;
    }
    if (isTokenExpiringSoon(token)) {
      // Fire-and-forget — failure handling lives in the response interceptor
      proactiveRefresh().catch(() => undefined);
    }
  }, BACKGROUND_REFRESH_POLL_MS);
};

/**
 * Stop the background refresh loop (call on logout).
 */
export const stopBackgroundRefresh = (): void => {
  if (backgroundRefreshTimer) {
    clearInterval(backgroundRefreshTimer);
    backgroundRefreshTimer = null;
  }
};

/**
 * Create a configured axios instance with automatic token refresh
 *
 * @param baseURL - The base URL for API calls
 * @returns Configured axios instance
 */
export const createApiClient = (baseURL: string) => {
  const client = axios.create({
    baseURL,
    timeout: 30_000,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  // Request interceptor: add token and refresh if expiring soon
  client.interceptors.request.use(
    async (config: InternalAxiosRequestConfig) => {
      let token = localStorage.getItem('accessToken');

      // Check if token exists and is about to expire
      if (token && isTokenExpiringSoon(token)) {
        // Don't log here — proactiveRefresh logs once per actual refresh.
        const newToken = await proactiveRefresh();
        if (newToken) {
          token = newToken;
        }
      }

      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }

      // Add Accept-Language header for i18n support
      config.headers['Accept-Language'] = i18n.language || 'zh-TW';

      // Send browser timezone so backend can compute timezone-aware streaks / daily boundaries
      config.headers['X-Timezone'] = Intl.DateTimeFormat().resolvedOptions().timeZone;

      return config;
    },
    (error) => Promise.reject(error)
  );

  // Response interceptor: handle 401 (fallback) and background refresh
  client.interceptors.response.use(
    (response) => {
      // On successful response, check if we should refresh token in background
      const token = localStorage.getItem('accessToken');
      if (token && isTokenExpiringSoon(token)) {
        proactiveRefresh().catch(() => {
          // Ignore errors, will be handled on next request
        });
      }
      return response;
    },
    async (error: AxiosError) => {
      const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

      // Check for invalid/missing token error - redirect to login immediately
      // Note: message can be string or array (from class-validator)
      const rawMessage = (error.response?.data as { message?: string | string[] })?.message;
      const errorMessage = Array.isArray(rawMessage) ? rawMessage.join(' ') : (rawMessage || '');
      const errorMessageLower = errorMessage.toLowerCase();
      const isInvalidToken = errorMessageLower.includes('invalid') &&
                             errorMessageLower.includes('token');
      const isMissingToken = errorMessageLower.includes('missing') &&
                             errorMessageLower.includes('token');

      if (error.response?.status === 401 && (isInvalidToken || isMissingToken)) {
        // Only force-redirect when the user HAD a token (expired/invalid).
        // Anonymous visitors calling a private endpoint also produce
        // "missing token" 401s — redirecting them breaks public pages
        // (course detail, browse) that happen to fire some authenticated
        // sub-request, and worse, kicks Googlebot's JS pass off the page
        // before it can index anything.
        const hadToken =
          !!localStorage.getItem('accessToken') ||
          !!localStorage.getItem('refreshToken');
        if (hadToken) {
          if (IS_DEV) console.log('🚫 Invalid or expired token, redirecting to login...');
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
          localStorage.removeItem('user');
          window.location.href = '/login';
        } else if (IS_DEV) {
          console.log('🔓 Anonymous 401 — letting caller handle it (no redirect).');
        }
        return Promise.reject(error);
      }

      // If 401 and not already retried, try to refresh
      if (error.response?.status === 401 && !originalRequest._retry) {
        originalRequest._retry = true;

        try {
          const newToken = await proactiveRefresh();

          if (newToken && originalRequest) {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            return client(originalRequest);
          }
        } catch (refreshError) {
          // Refresh failed, clear tokens and redirect to login
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
          localStorage.removeItem('user');
          window.location.href = '/login';
          return Promise.reject(refreshError);
        }
      }

      return Promise.reject(error);
    }
  );

  // Retry interceptor: retry idempotent requests on network/5xx errors
  client.interceptors.response.use(undefined, async (error: AxiosError) => {
    const config = error.config as InternalAxiosRequestConfig & { _retryCount?: number };
    if (!config) return Promise.reject(error);

    const method = (config.method || '').toUpperCase();
    const isIdempotent = ['GET', 'HEAD', 'OPTIONS'].includes(method);
    const isNetworkError = !error.response;
    const isServerError = error.response && error.response.status >= 500;

    if (!isIdempotent || (!isNetworkError && !isServerError)) {
      return Promise.reject(error);
    }

    const retryCount = config._retryCount || 0;
    const maxRetries = 2;

    if (retryCount >= maxRetries) {
      // Show toast after exhausting retries
      try {
        const msg = isNetworkError
          ? i18n.t('common:network.networkError')
          : i18n.t('common:network.serverError');
        useToastStore.getState().error(msg);
      } catch {
        // Toast store may not be ready during initial load
      }
      return Promise.reject(error);
    }

    config._retryCount = retryCount + 1;
    const delay = 1000 * Math.pow(2, retryCount); // 1s, 2s
    await new Promise((resolve) => setTimeout(resolve, delay));
    return client(config);
  });

  return client;
};

// Pre-configured API clients for different base URLs
export const apiClient = createApiClient(`${API_BASE_URL}/api/v1`);
export const creatorApiClient = createApiClient(`${API_BASE_URL}/v1`);
export const baseApiClient = createApiClient(API_BASE_URL);

export default apiClient;
